import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Select,
  Space,
  Steps,
  Switch,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';

const { Text } = Typography;
const { TextArea } = Input;

const API_BASE_URL = '/api/admin/partner-shops';
const SERVICE_WEB_URL = 'http://localhost:5173';

type LookbookSection = {
  category: string;
  photos: any[];
};

const INITIAL_REVIEW_SUMMARY = {
  totalMentionCount: 0,
  evaluatedReviewCount: 0,
  directReviewCount: 0,
  indirectReviewCount: 0,
  mentionedOnlyCount: 0,
  summaryText: '',
  prosJson: [],
  consJson: [],
  keywordsJson: [],
  sourceCafeArticleIdsJson: [],
  status: 'READY',
};

function createEmptyDraft() {
  return {
    shop: {
      shopName: '',
      displayName: '',
      shopSlug: '',
      shopCategory: '예복',
      branchName: '',
      areaName: '',
      shortDescription: '',
      brandStory: '',
      address: '',
      openingHours: '',
      parkingInfo: '',
      reservationMethod: '',
      consultationMethod: '',
      phone: '',
      homepageUrl: '',
      naverPlaceId: '',
      googleMapUrl: '',
      isCertified: 1,
      status: 'DRAFT',
      sortOrder: 0,
      featuresJson: [],
      priceItemsJson: [],
      faqJson: [],
      noticeJson: [],
    },
    extractedShopMaps: [],
    reviewSummary: INITIAL_REVIEW_SUMMARY,
    reviewPhotos: [],
    reviewItems: [],
    lookbookSections: [],
    products: [],
    faqs: [],
    pageData: null,
  };
}

function buildPageData(draft: any) {
  const shop = draft.shop || {};
  const reviewSummary = draft.reviewSummary || {};
  const reviewPhotos = draft.reviewPhotos || [];
  const lookbookSections = draft.lookbookSections || [];
  const products = draft.products || [];
  const faqs = draft.faqs || [];

  const publishedReviewPhotos = reviewPhotos.filter((photo: any) => Number(photo.isPublished) === 1);

  const lookbookUrls = lookbookSections
    .flatMap((section: any) => section.photos || [])
    .filter((photo: any) => photo.isPublished !== false)
    .map((photo: any) => photo.imageUrlW720 || photo.imageUrl || photo.thumbnailUrl)
    .filter(Boolean);

  return {
    shop: {
      name: shop.displayName || shop.shopName || '',
      category: [shop.shopCategory].filter(Boolean),
      description: shop.shortDescription || '',
      location: shop.areaName || '',
      reviewCount: Number(reviewSummary.totalMentionCount || 0),
      photoCount: publishedReviewPhotos.length,
      verified: Number(shop.isCertified) === 1,
    },
    heroImages: {
      mobile: lookbookUrls[0] || publishedReviewPhotos[0]?.imageUrlW720 || publishedReviewPhotos[0]?.imageUrl || '',
      desktop: lookbookUrls[0] || publishedReviewPhotos[0]?.imageUrlW720 || publishedReviewPhotos[0]?.imageUrl || '',
      gallery: lookbookUrls.slice(0, 4),
    },
    brandStory: {
      description: shop.brandStory || '',
      points: (shop.featuresJson || []).map((item: any) => ({
        title: item.title || '',
        desc: item.description || item.desc || '',
      })),
    },
    whyThisBrand: (reviewSummary.prosJson || []).map((item: any) => ({
      title: item.title || '',
      desc: item.description || '',
    })),
    reviewSummary: {
      totalReviewCount: Number(reviewSummary.totalMentionCount || 0),
      quote: reviewSummary.summaryText || '',
      pros: reviewSummary.prosJson || [],
      notes: reviewSummary.consJson || [],
    },
    reviewPhotos: publishedReviewPhotos.map((photo: any) => ({
      id: photo.id || photo.tempId || photo.cafeArticleImageId,
      src: photo.imageUrlW720 || photo.imageUrl,
      label: photo.photoCategory || '미분류',
      category: photo.photoCategory || '미분류',
    })),
    brandLookbook: lookbookUrls.slice(0, 8),
    products,
    faqs,
    information: [
      { label: '위치', value: shop.address || '' },
      { label: '운영시간', value: shop.openingHours || '' },
      { label: '주차', value: shop.parkingInfo || '' },
      { label: '예약 방식', value: shop.reservationMethod || '' },
      { label: '상담 방식', value: shop.consultationMethod || '' },
    ],
  };
}

export default function PartnerShopCreateEditorPage() {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [form] = Form.useForm();

  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const [draft, setDraft] = useState<any>(() => {
    const empty = createEmptyDraft();
    return {
      ...empty,
      pageData: buildPageData(empty),
    };
  });

  const [extractedShopOptions, setExtractedShopOptions] = useState<any[]>([]);
  const [selectedExtractedShopIds, setSelectedExtractedShopIds] = useState<number[]>([]);

  const [lookbookModalOpen, setLookbookModalOpen] = useState(false);
  const [selectedLookbookSectionIndex, setSelectedLookbookSectionIndex] = useState(0);

  const previewUrl = useMemo(() => {
    return `${SERVICE_WEB_URL}/preview/partner-shops/__new__`;
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      ...draft.shop,
      reviewSummary: draft.reviewSummary,
      products: draft.products,
      faqs: draft.faqs,
    });
  }, []);

  useEffect(() => {
    if (!draft.pageData) return;

    const timer = setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: 'PARTNER_SHOP_PREVIEW_UPDATE',
          payload: draft.pageData,
        },
        SERVICE_WEB_URL,
      );
    }, 80);

    return () => clearTimeout(timer);
  }, [draft.pageData]);

  async function searchExtractedShops(keyword: string) {
    const res = await axios.get(`${API_BASE_URL}/extracted-shops/search`, {
      params: { keyword },
    });

    setExtractedShopOptions(res.data.data || []);
  }

  function patchDraft(patch: any) {
    setDraft((prev: any) => {
      const next = {
        ...prev,
        ...patch,
      };

      return {
        ...next,
        pageData: buildPageData(next),
      };
    });
  }

  function rebuildDraftFromForm(extra: any = {}) {
    const values = form.getFieldsValue(true);

    const nextDraft = {
      ...draft,
      ...extra,
      shop: {
        ...draft.shop,
        ...values,
        priceItemsJson: values.products || [],
        faqJson: values.faqs || [],
      },
      reviewSummary: values.reviewSummary || draft.reviewSummary,
      products: values.products || [],
      faqs: values.faqs || [],
    };

    patchDraft(nextDraft);
  }

  async function loadAiConnectedData() {
    if (selectedExtractedShopIds.length === 0) {
      message.warning('연결할 AI 추출 업체를 먼저 선택하세요.');
      return;
    }

    setAiLoading(true);

    try {
      const res = await axios.post(`${API_BASE_URL}/create/ai-preview`, {
        cafeExtractedShopIds: selectedExtractedShopIds,
      });

      const aiData = res.data.data;

      const nextDraft = {
        ...draft,
        extractedShopMaps: selectedExtractedShopIds.map((id, index) => ({
          cafeExtractedShopId: id,
          relationType: index === 0 ? 'PRIMARY' : 'ALIAS',
          memo: '',
        })),
        reviewSummary: aiData.reviewSummary || INITIAL_REVIEW_SUMMARY,
        reviewPhotos: aiData.reviewPhotos || [],
        reviewItems: aiData.reviewItems || [],
      };

      patchDraft(nextDraft);

      form.setFieldsValue({
        ...form.getFieldsValue(true),
        reviewSummary: nextDraft.reviewSummary,
      });

      message.success('AI 연결 데이터와 후기 사진을 불러왔습니다.');
      setCurrentStep(1);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'AI 연결 데이터를 불러오지 못했습니다.');
    } finally {
      setAiLoading(false);
    }
  }

  function updateReviewPhoto(index: number, patch: any) {
    const nextReviewPhotos = (draft.reviewPhotos || []).map((photo: any, i: number) => {
      if (i !== index) return photo;
      return { ...photo, ...patch };
    });

    patchDraft({
      reviewPhotos: nextReviewPhotos,
    });
  }

  function addLookbookSection() {
    const nextSections = [
      ...(draft.lookbookSections || []),
      {
        category: `새 섹션 ${(draft.lookbookSections || []).length + 1}`,
        photos: [],
      },
    ];

    setSelectedLookbookSectionIndex(nextSections.length - 1);
    patchDraft({ lookbookSections: nextSections });
  }

  function updateLookbookSection(index: number, patch: any) {
    const nextSections = (draft.lookbookSections || []).map((section: any, i: number) => {
      if (i !== index) return section;
      return { ...section, ...patch };
    });

    patchDraft({ lookbookSections: nextSections });
  }

  function addLookbookFiles(fileList: UploadFile[]) {
    const nextSections = (draft.lookbookSections || []).map((section: any, index: number) => {
      if (index !== selectedLookbookSectionIndex) return section;

      const nextPhotos = [
        ...(section.photos || []),
        ...fileList.map((file, i) => ({
          tempId: `${Date.now()}-${i}`,
          title: file.name,
          description: '',
          thumbnailUrl: file.thumbUrl || '',
          imageUrl: file.thumbUrl || '',
          file,
          isPublished: true,
          sortOrder: (section.photos || []).length + i,
        })),
      ];

      return {
        ...section,
        photos: nextPhotos,
      };
    });

    patchDraft({ lookbookSections: nextSections });
  }

  async function submitCreate() {
    const values = await form.validateFields();

    setSaving(true);

    try {
      const payload = {
        shop: {
          ...draft.shop,
          ...values,
          priceItemsJson: values.products || [],
          faqJson: values.faqs || [],
        },
        extractedShopMaps: draft.extractedShopMaps || [],
        reviewSummary: values.reviewSummary || draft.reviewSummary,
        reviewPhotos: draft.reviewPhotos || [],
        reviewItems: draft.reviewItems || [],
        lookbookSections: draft.lookbookSections || [],
        products: values.products || [],
        faqs: values.faqs || [],
      };

      const res = await axios.post(`${API_BASE_URL}`, payload);

      message.success('업체 상세 페이지를 생성했습니다.');

      const saved = res.data.data;

      if (saved?.shop?.shopSlug) {
        window.location.href = `/admin/partner-shops/editor/${saved.shop.shopSlug}`;
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '생성 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  }

  const steps = [
    {
      title: 'AI 연결',
      description: '후기·사진 원천 연결',
    },
    {
      title: '기본정보',
      description: '상호와 소개 입력',
    },
    {
      title: '후기요약',
      description: '장단점 문구 편집',
    },
    {
      title: '후기사진',
      description: '노출 사진 검수',
    },
    {
      title: '룩북/구성',
      description: '공식 사진과 상품 구성',
    },
    {
      title: '미리보기',
      description: '최종 확인 후 생성',
    },
  ];

  return (
    <div style={{ height: '100vh', overflow: 'hidden', background: '#f5f5f5' }}>
      <div
        style={{
          height: 56,
          background: '#fff',
          borderBottom: '1px solid #eee',
          padding: '0 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Space>
          <Text strong style={{ fontSize: 16 }}>
            신규 업체 상세 페이지 생성
          </Text>
          <Tag color="green">Create Mode</Tag>
        </Space>

        <Space>
          <Button onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}>
            이전
          </Button>

          {currentStep < steps.length - 1 ? (
            <Button type="primary" onClick={() => setCurrentStep(currentStep + 1)}>
              다음
            </Button>
          ) : (
            <Button type="primary" loading={saving} onClick={submitCreate}>
              생성
            </Button>
          )}
        </Space>
      </div>

      <div style={{ display: 'flex', height: 'calc(100vh - 56px)' }}>
        <div
          style={{
            width: 680,
            minWidth: 680,
            overflow: 'auto',
            background: '#fafafa',
            borderRight: '1px solid #e5e5e5',
            padding: 16,
          }}
        >
          <Card size="small" style={{ marginBottom: 16 }}>
            <Steps
              current={currentStep}
              direction="vertical"
              size="small"
              items={steps}
              onChange={setCurrentStep}
            />
          </Card>

          <Form
            form={form}
            layout="vertical"
            onValuesChange={() => rebuildDraftFromForm()}
          >
            {currentStep === 0 && (
              <Card
                title="1. AI 데이터 연결"
                size="small"
                extra={
                  <Button type="primary" loading={aiLoading} onClick={loadAiConnectedData}>
                    선택한 업체로 데이터 불러오기
                  </Button>
                }
              >
                <Text type="secondary">
                  먼저 크롤링/LLM 분석에서 추출된 업체를 연결하세요.
                  이 연결을 기준으로 후기 요약과 후기 사진 후보를 불러옵니다.
                </Text>

                <Divider />

                <Select
                  mode="multiple"
                  showSearch
                  filterOption={false}
                  placeholder="예: 헤리츠테일러"
                  value={selectedExtractedShopIds}
                  onSearch={searchExtractedShops}
                  onChange={setSelectedExtractedShopIds}
                  style={{ width: '100%' }}
                  options={extractedShopOptions.map((item) => ({
                    label: `${item.shopName} · ${item.shopCategory || '미분류'}`,
                    value: item.id,
                  }))}
                />

                <Divider />

                {selectedExtractedShopIds.length === 0 ? (
                  <Empty description="아직 연결된 AI 추출 업체가 없습니다." />
                ) : (
                  <Space wrap>
                    {selectedExtractedShopIds.map((id) => {
                      const option = extractedShopOptions.find((item) => Number(item.id) === Number(id));

                      return (
                        <Tag key={id} color="blue">
                          {option?.shopName || `#${id}`}
                        </Tag>
                      );
                    })}
                  </Space>
                )}

                <Divider />

                <Text type="secondary">
                  연결 후 불러오는 데이터:
                  후기 요약, 좋았던 점, 아쉬운 점, 후기 사진 후보, 후기 카드 후보
                </Text>
              </Card>
            )}

            {currentStep === 1 && (
              <Card title="2. 기본정보" size="small">
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="shopName"
                      label="상호명"
                      rules={[{ required: true, message: '상호명을 입력하세요.' }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="displayName"
                      label="서비스 노출명"
                      rules={[{ required: true, message: '서비스 노출명을 입력하세요.' }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="shopSlug"
                      label="서비스 주소"
                      rules={[{ required: true, message: '서비스 주소를 입력하세요.' }]}
                    >
                      <Input addonBefore="/shops/" placeholder="heritz-tailor-cheongdam" />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item name="shopCategory" label="카테고리">
                      <Select
                        options={[
                          { label: '예복', value: '예복' },
                          { label: '드레스', value: '드레스' },
                          { label: '스튜디오', value: '스튜디오' },
                          { label: '메이크업', value: '메이크업' },
                          { label: '한복', value: '한복' },
                          { label: '예물', value: '예물' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="branchName" label="지점명">
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item name="areaName" label="지역">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="shortDescription" label="한 줄 소개">
                  <TextArea rows={3} />
                </Form.Item>

                <Form.Item name="brandStory" label="브랜드 스토리">
                  <TextArea rows={8} />
                </Form.Item>

                <Divider />

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="address" label="주소">
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item name="phone" label="전화번호">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="openingHours" label="운영시간">
                  <Input />
                </Form.Item>

                <Form.Item name="parkingInfo" label="주차">
                  <Input />
                </Form.Item>

                <Form.Item name="reservationMethod" label="예약 방식">
                  <Input />
                </Form.Item>

                <Form.Item name="consultationMethod" label="상담 방식">
                  <Input />
                </Form.Item>
              </Card>
            )}

            {currentStep === 2 && (
              <Card title="3. 후기 요약" size="small">
                <Row gutter={12}>
                  <Col span={8}>
                    <Form.Item name={['reviewSummary', 'totalMentionCount']} label="관련 후기 수">
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item name={['reviewSummary', 'evaluatedReviewCount']} label="구체 평가 수">
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item name={['reviewSummary', 'mentionedOnlyCount']} label="언급만">
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name={['reviewSummary', 'summaryText']} label="AI 요약 문장">
                  <TextArea rows={5} />
                </Form.Item>

                <Divider />

                <Text strong>좋았던 점</Text>

                <Form.List name={['reviewSummary', 'prosJson']}>
                  {(fields, { add, remove }) => (
                    <div style={{ marginTop: 12 }}>
                      {fields.map((field) => (
                        <Card
                          size="small"
                          key={field.key}
                          style={{ marginBottom: 12 }}
                          extra={
                            <Button size="small" danger onClick={() => remove(field.name)}>
                              삭제
                            </Button>
                          }
                        >
                          <Form.Item name={[field.name, 'title']} label="제목">
                            <Input />
                          </Form.Item>

                          <Form.Item name={[field.name, 'description']} label="설명">
                            <TextArea rows={3} />
                          </Form.Item>
                        </Card>
                      ))}

                      <Button block onClick={() => add({ title: '', description: '' })}>
                        좋았던 점 추가
                      </Button>
                    </div>
                  )}
                </Form.List>

                <Divider />

                <Text strong>아쉬운 점</Text>

                <Form.List name={['reviewSummary', 'consJson']}>
                  {(fields, { add, remove }) => (
                    <div style={{ marginTop: 12 }}>
                      {fields.map((field) => (
                        <Card
                          size="small"
                          key={field.key}
                          style={{ marginBottom: 12 }}
                          extra={
                            <Button size="small" danger onClick={() => remove(field.name)}>
                              삭제
                            </Button>
                          }
                        >
                          <Form.Item name={[field.name, 'title']} label="제목">
                            <Input />
                          </Form.Item>

                          <Form.Item name={[field.name, 'description']} label="설명">
                            <TextArea rows={3} />
                          </Form.Item>
                        </Card>
                      ))}

                      <Button block onClick={() => add({ title: '', description: '' })}>
                        아쉬운 점 추가
                      </Button>
                    </div>
                  )}
                </Form.List>
              </Card>
            )}

            {currentStep === 3 && (
              <Card title="4. 후기 사진 검수" size="small">
                <Text type="secondary">
                  AI 연결 후 불러온 사진입니다. 기본은 전체 노출이며, 이상한 사진만 숨김 처리하세요.
                </Text>

                <Divider />

                {(draft.reviewPhotos || []).length === 0 ? (
                  <Empty description="AI 연결을 먼저 진행해야 후기 사진이 표시됩니다." />
                ) : (
                  <Row gutter={[12, 12]}>
                    {(draft.reviewPhotos || []).map((photo: any, index: number) => (
                      <Col span={8} key={photo.id || photo.cafeArticleImageShopId || index}>
                        <Card
                          size="small"
                          bodyStyle={{ padding: 8 }}
                          cover={
                            <div style={{ aspectRatio: '1 / 1', overflow: 'hidden' }}>
                              <Image
                                src={photo.imageUrlW720 || photo.imageUrl}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                            </div>
                          }
                        >
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <Checkbox
                              checked={Number(photo.isPublished) === 1}
                              onChange={(e) => {
                                updateReviewPhoto(index, {
                                  isPublished: e.target.checked ? 1 : 0,
                                });
                              }}
                            >
                              노출
                            </Checkbox>

                            <Input
                              size="small"
                              value={photo.photoCategory || ''}
                              placeholder="분류"
                              onChange={(e) => {
                                updateReviewPhoto(index, {
                                  photoCategory: e.target.value,
                                });
                              }}
                            />

                            <Input
                              size="small"
                              value={photo.caption || ''}
                              placeholder="캡션"
                              onChange={(e) => {
                                updateReviewPhoto(index, {
                                  caption: e.target.value,
                                });
                              }}
                            />
                          </Space>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </Card>
            )}

            {currentStep === 4 && (
              <Card title="5. 룩북 / 구성안내" size="small">
                <Space style={{ marginBottom: 12 }}>
                  <Button type="primary" onClick={() => setLookbookModalOpen(true)}>
                    룩북 섹션 편집
                  </Button>
                  <Text type="secondary">공식 사진은 후기 사진과 별도로 관리합니다.</Text>
                </Space>

                {(draft.lookbookSections || []).length === 0 ? (
                  <Empty description="등록된 룩북 섹션이 없습니다." />
                ) : (
                  <Row gutter={[12, 12]}>
                    {(draft.lookbookSections || []).map((section: any, index: number) => (
                      <Col span={12} key={`${section.category}-${index}`}>
                        <Card size="small" title={section.category}>
                          <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
                            {(section.photos || []).slice(0, 5).map((photo: any, photoIndex: number) => (
                              <Image
                                key={photo.id || photo.tempId || photoIndex}
                                src={photo.thumbnailUrl || photo.imageUrlW720 || photo.imageUrl}
                                width={72}
                                height={72}
                                style={{ objectFit: 'cover', borderRadius: 8 }}
                              />
                            ))}
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}

                <Divider />

                <Text strong>구성안내</Text>

                <Form.List name="products">
                  {(fields, { add, remove }) => (
                    <div style={{ marginTop: 12 }}>
                      {fields.map((field) => (
                        <Card
                          key={field.key}
                          size="small"
                          style={{ marginBottom: 12 }}
                          extra={
                            <Button size="small" danger onClick={() => remove(field.name)}>
                              삭제
                            </Button>
                          }
                        >
                          <Form.Item name={[field.name, 'title']} label="구성명">
                            <Input placeholder="맞춤 예복" />
                          </Form.Item>

                          <Form.Item name={[field.name, 'description']} label="설명">
                            <Input placeholder="촬영 + 본식 포함" />
                          </Form.Item>

                          <Form.Item name={[field.name, 'price']} label="가격">
                            <Input placeholder="1,290,000원 ~" />
                          </Form.Item>
                        </Card>
                      ))}

                      <Button block onClick={() => add({ title: '', description: '', price: '' })}>
                        구성 추가
                      </Button>
                    </div>
                  )}
                </Form.List>
              </Card>
            )}

            {currentStep === 5 && (
              <Card title="6. 미리보기 / 생성" size="small">
                <Text>
                  우측 미리보기를 확인한 뒤 생성 버튼을 누르면 서비스 노출용 업체 데이터가 생성됩니다.
                </Text>

                <Divider />

                <Space>
                  <Button onClick={() => setCurrentStep(0)}>
                    AI 연결 다시 보기
                  </Button>

                  <Button type="primary" loading={saving} onClick={submitCreate}>
                    업체 생성
                  </Button>
                </Space>
              </Card>
            )}
          </Form>
        </div>

        <div style={{ flex: 1, minWidth: 0, height: '100%', overflow: 'hidden' }}>
          <div
            style={{
              height: 44,
              background: '#fff',
              borderBottom: '1px solid #eee',
              padding: '0 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Text strong>실시간 미리보기</Text>
            <Text type="secondary">{previewUrl}</Text>
          </div>

          <iframe
            ref={iframeRef}
            src={previewUrl}
            title="partner-shop-preview"
            style={{
              width: '100%',
              height: 'calc(100% - 44px)',
              border: 0,
              display: 'block',
              background: '#fff',
            }}
          />
        </div>
      </div>

      <Modal
        title="룩북 섹션 편집"
        open={lookbookModalOpen}
        width={1000}
        onCancel={() => setLookbookModalOpen(false)}
        onOk={() => setLookbookModalOpen(false)}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, minHeight: 520 }}>
          <Card
            size="small"
            title="섹션"
            extra={<Button size="small" onClick={addLookbookSection}>추가</Button>}
          >
            <List
              dataSource={draft.lookbookSections || []}
              renderItem={(section: any, index: number) => (
                <List.Item
                  style={{
                    cursor: 'pointer',
                    background: selectedLookbookSectionIndex === index ? '#f0f5ff' : undefined,
                    padding: 8,
                    borderRadius: 8,
                  }}
                  onClick={() => setSelectedLookbookSectionIndex(index)}
                >
                  <Input
                    value={section.category}
                    onChange={(e) => {
                      updateLookbookSection(index, {
                        category: e.target.value,
                      });
                    }}
                  />
                </List.Item>
              )}
            />
          </Card>

          <Card size="small" title="사진">
            {draft.lookbookSections?.[selectedLookbookSectionIndex] ? (
              <>
                <Upload.Dragger
                  multiple
                  beforeUpload={() => false}
                  showUploadList={false}
                  onChange={(info) => addLookbookFiles(info.fileList)}
                >
                  <p className="ant-upload-text">이미지를 선택하세요</p>
                  <p className="ant-upload-hint">
                    지금 바로 업로드하지 않고 생성 시점에 처리합니다.
                  </p>
                </Upload.Dragger>

                <Divider />

                <Row gutter={[12, 12]}>
                  {(draft.lookbookSections[selectedLookbookSectionIndex].photos || []).map((photo: any, photoIndex: number) => (
                    <Col span={8} key={photo.id || photo.tempId || photoIndex}>
                      <Card
                        size="small"
                        cover={
                          <div style={{ aspectRatio: '1 / 1', overflow: 'hidden' }}>
                            <Image
                              src={photo.thumbnailUrl || photo.imageUrlW720 || photo.imageUrl}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          </div>
                        }
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Input
                            size="small"
                            placeholder="제목"
                            value={photo.title || ''}
                            onChange={(e) => {
                              const nextSections = (draft.lookbookSections || []).map((section: any, sectionIndex: number) => {
                                if (sectionIndex !== selectedLookbookSectionIndex) return section;

                                return {
                                  ...section,
                                  photos: section.photos.map((item: any, i: number) =>
                                    i === photoIndex ? { ...item, title: e.target.value } : item,
                                  ),
                                };
                              });

                              patchDraft({ lookbookSections: nextSections });
                            }}
                          />

                          <Input
                            size="small"
                            placeholder="설명"
                            value={photo.description || ''}
                            onChange={(e) => {
                              const nextSections = (draft.lookbookSections || []).map((section: any, sectionIndex: number) => {
                                if (sectionIndex !== selectedLookbookSectionIndex) return section;

                                return {
                                  ...section,
                                  photos: section.photos.map((item: any, i: number) =>
                                    i === photoIndex ? { ...item, description: e.target.value } : item,
                                  ),
                                };
                              });

                              patchDraft({ lookbookSections: nextSections });
                            }}
                          />

                          <Switch
                            checkedChildren="노출"
                            unCheckedChildren="숨김"
                            checked={photo.isPublished !== false}
                            onChange={(checked) => {
                              const nextSections = (draft.lookbookSections || []).map((section: any, sectionIndex: number) => {
                                if (sectionIndex !== selectedLookbookSectionIndex) return section;

                                return {
                                  ...section,
                                  photos: section.photos.map((item: any, i: number) =>
                                    i === photoIndex ? { ...item, isPublished: checked } : item,
                                  ),
                                };
                              });

                              patchDraft({ lookbookSections: nextSections });
                            }}
                          />
                        </Space>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </>
            ) : (
              <Empty description="섹션을 먼저 추가하세요." />
            )}
          </Card>
        </div>
      </Modal>
    </div>
  );
}